import { useMemo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { FaChevronLeft, FaChevronDown } from "react-icons/fa6";
import { HierarchicalConfig, ParentItem } from "./index";

interface LevelNavigationProps {
  config: HierarchicalConfig;
  parentItems: ParentItem[];
  onNavigateToRoot: () => void;
  onParentSelect: (item: ParentItem) => void;
  isChildLevel: boolean;
}

const LevelNavigation = ({
  config,
  parentItems,
  onNavigateToRoot,
  onParentSelect,
  isChildLevel,
}: LevelNavigationProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Label from API; if area_name was dropped from the query (e.g. unencoded
  // spaces), resolve from cached root-level parent items using area_id.
  const currentParentLabel = useMemo(() => {
    const pd = config.parent_dropdown;
    if (!pd?.enabled) return "Select...";
    const fromApi = pd.current_label?.trim();
    if (fromApi) return fromApi;
    const match = parentItems.find(
      (item) => String(item.id) === String(pd.current_value)
    );
    return match?.name?.trim() || "Select...";
  }, [config.parent_dropdown, parentItems]);

  // Should show parent dropdown
  const showParentDropdown = useMemo(() => {
    return (
      isChildLevel &&
      config.parent_dropdown?.enabled &&
      parentItems.length > 0
    );
  }, [isChildLevel, config.parent_dropdown, parentItems]);

  if (!isChildLevel) {
    return null;
  }

  return (
    <NavigationWrapper>
      {/* Back button */}
      <BackButton onClick={onNavigateToRoot} title="Back to all items">
        <FaChevronLeft />
        <span>Back</span>
      </BackButton>

      {/* Parent dropdown */}
      {showParentDropdown && (
        <DropdownContainer ref={dropdownRef}>
          <DropdownTrigger
            onClick={() => setDropdownOpen(!dropdownOpen)}
            $isOpen={dropdownOpen}
          >
            <span className="label">{currentParentLabel}</span>
            <FaChevronDown className="icon" />
          </DropdownTrigger>

          {dropdownOpen && (
            <DropdownMenu>
              {parentItems.map((item) => (
                <DropdownItem
                  key={item.id}
                  onClick={() => {
                    onParentSelect(item);
                    setDropdownOpen(false);
                  }}
                  $isActive={
                    String(item.id) ===
                    String(config.parent_dropdown?.current_value)
                  }
                >
                  {item.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          )}
        </DropdownContainer>
      )}
    </NavigationWrapper>
  );
};

const NavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 8px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: var(--primary-color, #0f80d1);
  background: transparent;
  border: 1px solid var(--primary-color, #0f80d1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--primary-color, #0f80d1);
    color: white;
  }

  svg {
    font-size: 10px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-color, #333);
  background: var(--widget-control-bg, #f5f5f5);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 4px;
  cursor: pointer;
  min-width: 120px;
  justify-content: space-between;

  .label {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .icon {
    font-size: 10px;
    transition: transform 0.2s;
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};
  }

  &:hover {
    border-color: var(--primary-color, #0f80d1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  min-width: 180px;
  max-height: 250px;
  overflow-y: auto;
  background: var(--widget-bg, white);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
`;

const DropdownItem = styled.button<{ $isActive: boolean }>`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 12px;
  text-align: left;
  color: var(--text-color, #333);
  background: ${({ $isActive }) =>
    $isActive ? "var(--primary-color-light, #e6f3ff)" : "transparent"};
  border: none;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--hover-bg, #f0f0f0);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color-light, #eee);
  }
`;

export default LevelNavigation;
